<template>
  <div class="w-full p-4">
    <div class="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
      <button
        type="button"
        class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        @click="close">
        <span class="sr-only">Close</span>
        <!-- Heroicon name: outline/x -->
        <svg
          class="size-6"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    <div class="sm:flex sm:items-start">
      <div class="mt-3 text-center sm:mt-0 sm:text-left">
        <h3 class="text-lg font-medium leading-6 text-gray-900">
          Add new Project
        </h3>
      </div>
    </div>
    <div class="my-8 flex w-full flex-col space-y-4">
      <div>
        <label
          for="name"
          class="block text-sm font-medium text-gray-700">Project Name</label>
        <div class="relative mt-1 rounded-md shadow-sm">
          <input
            id="name"
            ref="NameInput"
            v-model="form.name"
            type="text"
            name="name"
            class="block w-full rounded-md pr-10 focus:outline-none sm:text-sm"
            :class="{'border-red-300 text-red-900 placeholder:text-red-300 focus:border-red-500 focus:ring-red-500': form.errors.name,
                     'border-gray-300 focus:border-indigo-300 focus:ring-indigo-200': !form.errors.name}"
            placeholder="Project"
            aria-invalid="true"
            aria-describedby="name-error"
            @change="reset">
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <!-- Heroicon name: solid/exclamation-circle -->
            <svg
              v-if="form.errors.name"
              class="size-5 text-red-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true">
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd" />
            </svg>
          </div>
        </div>
        <p
          v-if="form.errors.name"
          id="email-error"
          class="mt-2 text-sm text-red-600">
          {{ form.errors.name }}
        </p>
      </div>
      <div>
        <label
          for="client-search"
          class="block text-sm font-medium text-gray-700">Client</label>
        <div class="relative mt-1">
          <input
            id="client-search"
            ref="clientInput"
            v-model="clientQuery"
            type="text"
            class="block w-full rounded-md border-gray-300 pr-10 focus:border-indigo-300 focus:outline-none focus:ring-indigo-200 sm:text-sm"
            placeholder="Search client..."
            @focus="onClientFocus"
            @input="onClientInput"
            @blur="onClientBlur">
        </div>
        <teleport to="body">
          <div
            v-if="state.isClientListOpen"
            ref="clientDropdown"
            :style="dropdownStyles"
            class="z-50 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white py-1
              text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            <div
              v-if="state.isLoadingClients"
              class="px-3 py-2 text-gray-500">
              Loading…
            </div>
            <template v-else>
              <button
                v-for="option in clients"
                :key="option.value"
                type="button"
                class="flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
                @mousedown.prevent="selectClient(option)">
                <span class="truncate">{{ option.text }}</span>
                <svg
                  v-if="form.client_id === option.value"
                  class="size-4 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <div
                v-if="clients.length === 0"
                class="px-3 py-2 text-gray-500">
                No clients found
              </div>
            </template>
          </div>
        </teleport>
        <p
          v-if="form.errors.client_id"
          class="mt-2 text-sm text-red-600">
          {{ form.errors.client_id }}
        </p>
      </div>
    </div>
    <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
      <button
        type="button"
        class="inline-flex w-full justify-center rounded-md border border-transparent
          bg-green-600 px-4 py-2 text-base font-medium text-white shadow-sm
          hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
            sm:ml-3 sm:w-auto sm:text-sm"
        @click="save">
        Add
      </button>
      <button
        type="button"
        class="mt-3 inline-flex w-full justify-center rounded-md border
          border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm
          hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
          sm:mt-0 sm:w-auto sm:text-sm"
        @click="close">
        Cancel
      </button>
    </div>
  </div>
</template>
<script setup>
import {
  ref, reactive, onMounted, onBeforeUnmount, nextTick,
} from 'vue';
import { useForm } from '@inertiajs/vue3';
import axios from 'axios';

// props and emits
const props = defineProps({
  client: {
    type: Object,
    // Use a factory for Object-typed props to satisfy vue/require-default-prop
    default: () => null,
  },
});
const emit = defineEmits(['close']);

// template refs
const NameInput = ref(null);
const clientInput = ref(null);
const clientDropdown = ref(null);

// state
const clients = ref([]);
const clientQuery = ref(props.client && props.client.name ? props.client.name : '');
const state = reactive({
  isClientListOpen: false,
  isLoadingClients: false,
  clientSearchDebounce: null,
});
const dropdownStyles = reactive({
  position: 'fixed',
  top: '0px',
  left: '0px',
  width: '0px',
});

// inertia form
const form = useForm({
  name: '',
  client_id: props.client && props.client.id ? props.client.id : null,
});

/**
 * Align the teleported dropdown to the client input element.
 * No-ops if the dropdown is closed or the input ref is missing.
 * @returns {void}
 */
function updateDropdownPosition() {
  if (!state.isClientListOpen) {
    return;
  }
  const inputEl = clientInput.value;
  if (!inputEl) {
    return;
  }
  const rect = inputEl.getBoundingClientRect();
  dropdownStyles.position = 'fixed';
  dropdownStyles.top = `${rect.bottom + 4}px`;
  dropdownStyles.left = `${rect.left}px`;
  dropdownStyles.width = `${rect.width}px`;
}

/**
 * Fetch clients from the API and populate the dropdown list.
 * @param {string} [search] Optional name filter.
 * @returns {Promise<void>}
 */
async function loadClients(search = '') {
  state.isLoadingClients = true;
  try {
    const params = search ? { search } : {};
    const response = await axios.get(route('api.clients'), { params });
    const data = Array.isArray(response && response.data) ? response.data : [];
    clients.value = data.map((c) => ({
      value: c.id,
      text: c.name,
      selected: props.client && props.client.id === c.id,
    }));
  } catch (e) {
    if (typeof console !== 'undefined' && console.error) {
      console.error('Failed to load clients', e);
    }
  } finally {
    state.isLoadingClients = false;
    updateDropdownPosition();
  }
}

/**
 * Open the client list when the input gains focus and position the dropdown.
 * If no options are loaded yet, fetch an initial page.
 * @returns {void}
 */
function onClientFocus() {
  state.isClientListOpen = true;
  nextTick(updateDropdownPosition);
  if (!clients.value || clients.value.length === 0) {
    loadClients('');
  }
}

/**
 * Debounce client search while typing and keep the list open.
 * @returns {void}
 */
function onClientInput() {
  state.isClientListOpen = true;
  if (state.clientSearchDebounce) {
    clearTimeout(state.clientSearchDebounce);
  }
  state.clientSearchDebounce = setTimeout(() => {
    loadClients(clientQuery.value);
  }, 250);
}

/**
 * Close the client dropdown shortly after blur to allow option clicks
 * via mousedown to register before the list disappears.
 * @returns {void}
 */
function onClientBlur() {
  // Delay closing to allow click selection in dropdown
  setTimeout(() => {
    state.isClientListOpen = false;
  }, 150);
}

/**
 * Select a client option and close the dropdown.
 * @param {ClientOption} option - The chosen client entry from the list.
 * @returns {void}
 */
function selectClient(option) {
  form.client_id = option.value;
  clientQuery.value = option.text;
  state.isClientListOpen = false;
}

/**
 * Handle outside clicks and close the dropdown when the click target is
 * neither the input nor the dropdown content.
 * @param {MouseEvent} e - The document mousedown event.
 * @returns {void}
 */
function onDocumentClick(e) {
  if (!state.isClientListOpen) {
    return;
  }
  const inputEl = clientInput.value;
  const dropdownEl = clientDropdown.value;
  if (!inputEl || !dropdownEl) {
    return;
  }
  const { target } = e;
  if (!inputEl.contains(target) && !dropdownEl.contains(target)) {
    state.isClientListOpen = false;
  }
}

/**
 * Close the dropdown on Escape key press.
 * @param {KeyboardEvent} e - Document keydown event.
 * @returns {void}
 */
function onKeydown(e) {
  if (e && e.key === 'Escape' && state.isClientListOpen) {
    state.isClientListOpen = false;
  }
}

/**
 * Emit a request to close the modal.
 * @returns {void}
 */
function close() {
  emit('close');
}

/**
 * Submit the create-project form to the backend.
 * Posts to the 'projects.store' route with current form data.
 * @returns {void}
 */
function save() {
  form.post(route('projects.store'), {
    errorBag: 'storeProject',
    preserveScroll: true,
  });
}

/**
 * Clear validation errors on the form.
 * Does not modify field values.
 * @returns {void}
 */
function reset() {
  form.errors = {};
}

// lifecycle
onMounted(() => {
  setTimeout(() => {
    if (NameInput.value && typeof NameInput.value.focus === 'function') {
      NameInput.value.focus();
    }
  }, 50);

  // Bind global listeners for positioning and closing
  window.addEventListener('resize', updateDropdownPosition);
  window.addEventListener('scroll', updateDropdownPosition, true);
  document.addEventListener('mousedown', onDocumentClick);
  document.addEventListener('keydown', onKeydown);

  // initial data
  loadClients();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateDropdownPosition);
  window.removeEventListener('scroll', updateDropdownPosition, true);
  document.removeEventListener('mousedown', onDocumentClick);
  document.removeEventListener('keydown', onKeydown);
  if (state.clientSearchDebounce) {
    clearTimeout(state.clientSearchDebounce);
    state.clientSearchDebounce = null;
  }
});

// expose to template
// In <script setup>, all refs/functions are auto-exposed
</script>
